import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getToken } from "context/auth";

import { ReactComponent as ThreeDotsIcon } from "assets/icons/ico_three_dots_green.svg";
import { ReactComponent as ShareIcon } from "assets/icons/ico_share.svg";
import { ReactComponent as ActivateIcon } from "assets/icons/ico_play_circle.svg";
import { ReactComponent as ClockIcon } from "assets/icons/ico_clock.svg";

import animSpinnerGreen from 'assets/animations/anim_spinner_green_40x40.json';
import Lottie from 'lottie-react';

import { useMessages } from "context/messages";
import TableItemContextMenu from "components/dashboard/tableItemContextMenu/itemContextMenu";
import { useNavigate, useParams } from "react-router-dom";
import { deletePermit, revokePermit, acceptManualActivatePermit } from "api/permits";
import formatDaysToDaySpan from "helpers/formatDaysToDaySpan";
import ButtonSlider from "components/common/buttonSlider";
import InputDate from "components/common/inputDate";
import moment from "moment";
import Tooltip from "components/common/tooltip";
import { PermitManualActivationModal } from "./permitManualActivationModal";
import { PermitHistoryModal } from "./permitHistoryModal";


/**
 * Visar SMS-leveransstatus för permit-inbjudan intill telefonnumret.
 * Visar inget för "delivered"/saknad status (håller tabellen ren — fokus på problemen).
 * Förklaringen (varför det inte gick) visas i en hover-ruta ovanför badgen — samma
 * mönster som Tooltip-komponenten redan använder i den här tabellen (group-hover).
 */
const SmsStatusBadge = ({ status, reasonCode, t }) => {
    if (!status || status === 'delivered') return null;

    const config = {
        failed: { cls: 'bg-red-100 text-red-700', label: t('smsStatus.failed'), tip: t(`smsStatus.reason.${reasonCode || 'unknown'}`) },
        retrying: { cls: 'bg-amber-100 text-amber-700', label: t('smsStatus.retrying'), tip: t('smsStatus.retryingTip') },
        pending: { cls: 'bg-airpark-gray-300 text-airpark-gray-500', label: t('smsStatus.pending'), tip: t('smsStatus.pendingTip') },
    }[status];

    if (!config) return null;

    return (
        <span className="group relative inline-flex items-center">
            <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap cursor-help ${config.cls}`}
            >
                {config.label}
            </span>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-30 hidden group-hover:block w-60 whitespace-normal rounded-xl border border-airpark-gray-300 bg-white p-3 text-xs font-normal text-left text-airpark-gray-900 shadow-lg">
                {config.tip}
            </span>
        </span>
    );
};


const PermitItem = ({ permit, permits, setPermits, reFetchData }) => {

    const { t } = useTranslation();
    const [showMenu, setShowMenu] = useState();
    const { showToast, showModal, hideModal } = useMessages();
    const [isDeleting, setIsDeleting] = useState();
    const [manualActivateModalVisible, setManualActivateModalVisible] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const navigate = useNavigate();
    const { organizationExtId, parkingAreaExtId } = useParams();
    const editPermitUrl = permit.originatesFromMarketplace ? `/${organizationExtId}/parking-areas/${parkingAreaExtId}/edit-marketplace-permit/${permit.permitExtId.toLowerCase()}` :
        `/${organizationExtId}/parking-areas/${parkingAreaExtId}/edit-permit/${permit.permitExtId.toLowerCase()}`;

    const baseCssClass = `pl-6 whitespace-nowrap ${permit.status === 'expired' ? 'text-gray-400' : 'text-black'}`;

    const reAddToMarketplaceRef = useRef(permit.reAddToMarketplace);

    // Uppsägningstiden är konfigurerbar per område (PermitDto.noticePeriodMonths, default 1).
    // Får INTE hårdkodas till 1 här — då lovar dialogen kunden ett annat slutdatum än det
    // backend faktiskt sätter. Formeln speglar NoticePeriodCalculator.GetLastActiveDate:
    // sista dagen i månaden N månader efter uppsägningsmånaden.
    const noticePeriodMonths = permit?.noticePeriodMonths ?? 1;
    const initialRevokeDate = permit?.endDateTime
        ? moment(permit.endDateTime).endOf('day').toDate()
        : moment().add(noticePeriodMonths, 'month').endOf('month').endOf('day').toDate();
    const permitRevokeDateRef = useRef(initialRevokeDate);


    const formatDate = (date) => {
        // Format date to yyyy-mm-dd
        return moment(date).format('YYYY-MM-DD')
    }

    const reAddedPermitStartDate = useRef(moment(permitRevokeDateRef.current).add(1, 'minute'));

    // Dialogen öppnas om (isReopen) när datum eller marknadsplatsval ändras — då ska valen behållas.
    // Öppnas den från menyn ska den utgå från tillståndets nuvarande värden: refs sätts bara vid
    // första render, så ett slutdatum som ändrats sedan dess skickades annars som override (tillstånd 1637).
    const handleRevokePermit = (isReopen = false) => {
        setShowMenu(false);

        if (!isReopen) {
            permitRevokeDateRef.current = initialRevokeDate;
            reAddedPermitStartDate.current = moment(initialRevokeDate).add(1, 'minute');
            reAddToMarketplaceRef.current = permit.reAddToMarketplace;
        }

        const canChangeEndDate = permit.originatesFromMarketplace;
        const descriptionKey = canChangeEndDate 
            ? 'permitTable.revokePermitDescription' 
            : 'permitTable.revokePermitDescriptionWithoutDateOption';

        showModal(
            t('permitTable.revokePermitTitle'),
            t(descriptionKey, { date: formatDate(permitRevokeDateRef.current), note: t('permitTable.revokePermitEndDateNote', { endDate: formatDate(permit?.endDateTime ? permit.endDateTime : initialRevokeDate)}) }),
            t('generic.yes'),
            async () => {       // on click
                setIsDeleting(true);
                hideModal();

                try {
                    const token = await getToken();

                    const revokePermitDto = {
                        reAddToMarketplace: reAddToMarketplaceRef.current,
                    };

                    if (!moment(permitRevokeDateRef.current).isSame(moment(initialRevokeDate))) {
                        revokePermitDto.endDateTimeOverride = permitRevokeDateRef.current;
                    }

                    const result = await revokePermit(token, permit.permitExtId, revokePermitDto);


                    if (result.error) {
                        console.error(result);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    } else {
                        /* Successful, update item with new end date */
                        const index = permits.findIndex(({ permitExtId }) => permitExtId === permit.permitExtId);

                        permits[index] = result;
                        setPermits([...permits]);

                        showToast(t('permitTable.revokedPermitTitle'), t('permitTable.revokedPermitDescription'), 'success');
                    }
                } catch (error) {
                    console.error(error);
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                }

                setIsDeleting(false);
            },
            t('generic.cancel'),
            () => {
                permitRevokeDateRef.current = initialRevokeDate;
                hideModal()
            },
            'warning',
            permit.originatesFromMarketplace ? () => {
                return (
                    <div className='my-2 flex flex-col'>
                        <div className='mb-4'>
                            <InputDate value={permitRevokeDateRef.current}
                                       onChange={(date) => {
                                           hideModal();
                                           const newRevokeDate = moment(date).endOf('day').toDate();
                                           permitRevokeDateRef.current = newRevokeDate
                                           reAddedPermitStartDate.current = moment(newRevokeDate).add(1, 'minute').toDate();
                                           handleRevokePermit(true);
                                       }} />
                        </div>
                        <div className='flex flex-row items-center'>
                            <div className='w-[90%]'>
                                <ButtonSlider
                                    title={t('createPermitPage.reAddToMarketplace')}
                                    onClick={() => {
                                        hideModal();
                                        reAddToMarketplaceRef.current = !reAddToMarketplaceRef.current;
                                        handleRevokePermit(true);
                                    }}
                                    isToggled={reAddToMarketplaceRef.current}
                                />
                            </div>
                            <div className='relative flex-1'>
                                <Tooltip tooltip={t('permitTable.reAddToMarketplaceTooltip', { startDate: formatDate(reAddedPermitStartDate.current)})} className={'bottom-[-20px]'}/>
                            </div>
                        </div>
                    </div>
                )
            } : null,
        );
    };

    const handleDeletePermit = () => {

        setShowMenu(false);

        showModal(
            t('permitTable.deletePermitTitle'),
            t('permitTable.deletePermitDescription'),
            t('generic.yes'),
            async () => {
                setIsDeleting(true);
                hideModal();

                try {
                    const token = await getToken();
                    const result = await deletePermit(token, permit.permitExtId);

                    if (result.error) {
                        console.error(result);
                        showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                    } else {
                        /* Successful, remove item from list */
                        const index = permits.findIndex(({ permitExtId }) => permitExtId === permit.permitExtId);

                        permits.splice(index, 1);
                        setPermits([...permits]);

                        showToast(t('permitTable.deletedTitle'), t('permitTable.deletedDescription'), 'success');
                    }
                } catch (error) {
                    console.error(error);
                    showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
                }

                setIsDeleting(false);
            },
            t('generic.cancel'),
            () => { hideModal() },
            'warning');
    }

    const handleManualActivate = async (manualActivateItem) => {
      setShowMenu(false);

      try {
        const token = await getToken();
        const result = await acceptManualActivatePermit(
          token,
          permit.permitExtId,
          manualActivateItem
        );

        if (result.error) {
          console.error(result);
          showToast(
            t("generic.errorTitle"),
            t("generic.errorDescription"),
            "error"
          );
        } else {
          showToast(
            t('createPermitPage.permitActivatedTitle'),
            t('createPermitPage.permitActivatedDescription'),
            "success"
          );
        }
        setManualActivateModalVisible(false);
        reFetchData();
      } catch (error) {
        console.error(error);
        showToast(
          t("generic.errorTitle"),
          t("generic.errorDescription"),
          "error"
        );
      }
    };

    const getFriendlyParkingSpotsDescription = (permit) => {
        const spots = permit.parkingSpots ?? [];

        // If no spots, return "Area parking"
        if (spots.length === 0) {
            return t('createPermitPage.allParkingSpots');
        }

        // Check if this is a whole area permit
        // Whole area permits have a virtual placeholder spot with description "Virtual parking spot for permit"
        const isWholeAreaPermit = spots.some(ps => ps.description === "Virtual parking spot for permit");
        
        if (isWholeAreaPermit) {
            return t('createPermitPage.allParkingSpots');
        }

        // Extract numbered spots
        const numbered = spots
            .filter(ps => ps.isNumbered && ps.spotIdentifier)
            .map(ps => ps.spotIdentifier)
            .sort((a, b) => a.localeCompare(b));

        // Check if there are unnumbered spots
        const hasUnnumberedSpots = spots.some(ps => !ps.isNumbered);

        // Construct final description
        const descriptionParts = [];
        
        // Add numbered spot identifiers
        if (numbered.length > 0) {
            descriptionParts.push(...numbered);
        }
        
        // Add "Not numbered" if there are unnumbered spots (no counter)
        if (hasUnnumberedSpots) {
            descriptionParts.push(t('createPermitPage.notNumbered'));
        }

        return descriptionParts.join(', ');
    };

    return (
        <>
        <tr className={`h-16 border-b-[1px] border-airpark-gray-300`}>
            <td className={baseCssClass}>{permit.permitExtId}</td>
            <td className={baseCssClass}>{getFriendlyParkingSpotsDescription(permit)}</td>
            <td className={baseCssClass}>{(permit.parkingSpots || []).map(ps => ps.objectID).filter(Boolean).join(', ') || '-'}</td>
            <td className={baseCssClass}>{ permit.isManualActivation && permit.status === 'pending' ? t('permitTable.manualactivation') : t(`permitTable.${permit.status}`)}</td>
            {permit.isMarketplace &&
                <td className={baseCssClass}>{`${permit.isCommunity ? t('permitTable.community') : t('permitTable.everyone')}`}</td>
            }
            {permit.isMarketplace &&
                <td className={baseCssClass}>{`${permit.reAddToMarketplace ? t('generic.yes') : t('generic.no')}`}</td>
            }
            {!permit.isMarketplace &&
                <>
                    <td className={baseCssClass}>{permit?.isManualActivation ? permit.manualActivatedEmail : permit.email}</td>
                    <td className={baseCssClass}>{permit?.isManualActivation ? permit.manualActivatedFirstName : permit.firstName}</td>
                    <td className={baseCssClass}>{permit?.isManualActivation ? permit.manualActivatedLastName : permit.lastName}</td>
                    <td className={baseCssClass}>
                        <div className="flex items-center gap-2">
                            <span>{permit.phone}</span>
                            <SmsStatusBadge status={permit.smsStatus} reasonCode={permit.smsFailureReasonCode} t={t} />
                        </div>
                    </td>
                </>
            }
            <td className={baseCssClass}>{permit.description}</td>
            {!permit.isMarketplace &&
                <td className={baseCssClass}>{permit.contractCode || '-'}</td>
            }
            {!permit.isMarketplace &&
                <td className={baseCssClass}>{permit?.isManualActivation ? permit.manualActivatedLicencePlate : permit.registrationIdentifier}</td>
            }

            <td className={baseCssClass}>{`${permit.endDateTime != null ? permit.friendlyPeriod : `${permit.friendlyPeriod} - ${t('permitTable.ongoing')}`}`}</td>
            {!permit.isRecurring &&
                <td className={baseCssClass} colSpan={2}>{t('parkingArea.asDefinedByPeriod')}</td>
            }
            {permit.isRecurring &&
                <>
                    <td className={baseCssClass}>{formatDaysToDaySpan(permit, t)}</td>
                    <td className={baseCssClass}>{permit.friendlyTime}</td>
                </>
            }
            <td className={baseCssClass}>{permit.isSublettingAllowed ? `${t('permitTable.allowed')} (${permit.isCommunity ? t('permitTable.community').toLowerCase() : t('permitTable.everyone').toLowerCase()})` : t('permitTable.notallowed')}</td>
            <td className={baseCssClass}>{permit.isPaymentHandledManually ? t('permitTable.thirdParty') : t('generic.airPark')}</td>
            <td className={baseCssClass}>
                {permit.isPaymentHandledManually
                    ? '—'
                    : (permit.friendlyPrice && permit.unit
                        ? `${permit.friendlyPrice}/${t(`priceUnit.${permit.unit}`)}`
                        : '—')}
            </td>
            {!permit.isMarketplace && <td className={`${baseCssClass} pr-6`}>{permit.originatesFromMarketplace ? t('generic.yes') : t('generic.no')}</td>}
            {!permit.isMarketplace && <td className={`${baseCssClass} pr-6`}>{permit.alreadyRevokedOrCancelled ? t('generic.yes') : t('generic.no')}</td>}
            {!permit.isMarketplace && (
                <td className={`${baseCssClass} pr-6`}>{permit.isManualActivation ? t('generic.yes') : t('generic.no')}</td>
            )}
            <td className="border-l-[1px] border-airpark-gray-300 min-w-[72px] w-[72px] relative">
                {isDeleting &&
                    <div className="flex h-16 w-full justify-center items-center">
                        <Lottie className="h-6 w-6" animationData={animSpinnerGreen} loop={true} />
                    </div>
                }
                {/* Icke-marketplace-tillstånd ska gå att redigera även efter återkallning; marketplace oförändrat (visade redan menyn). */}
                {!isDeleting &&
                    <button className="flex h-16 w-full justify-center items-center" onClick={(e) => { e.preventDefault(); setShowMenu(!showMenu); }}>
                        <ThreeDotsIcon className="block" />
                    </button>
                }
                {showMenu &&
                    <TableItemContextMenu
                        onCloseClick={() => { setShowMenu(false); }}
                        onEditClick={ (!permit.alreadyRevokedOrCancelled || !permit.isMarketplace) ? ()=> { navigate(editPermitUrl)} : null }
                        onDeleteClick={
                            !permit.userExtId ? () => {
                                handleDeletePermit();
                            } : (!permit.alreadyRevokedOrCancelled && permit.status !== 'expired') ? () => {
                                handleRevokePermit();
                            } : null}
                        deleteTitle={permit.userExtId ? t('generic.revoke') : t('generic.remove')}>
                        <button className="flex items-center" data-testid="permit-history-menu-button" onClick={(e) => { e.preventDefault(); setShowMenu(false); setHistoryModalVisible(true); }} >
                            <ClockIcon className="h-5 w-5" /><span className="ml-1">{t('permitHistory.menuItem')}</span>
                        </button>
                        {permit.isMarketplace &&
                            <button className="flex items-center" onClick={(e) => { e.preventDefault(); navigate(`/${organizationExtId}/parking-areas/${parkingAreaExtId}/share-marketplace-permit/${permit.permitExtId}`); }} >
                                <ShareIcon className="h-5 w-5" /><span className="ml-1">{t('generic.share')}</span>
                            </button>
                        }
                        {permit.isManualActivation && permit.status === 'pending' &&
                            <button className="flex items-center" onClick={(e) => { e.preventDefault(); setManualActivateModalVisible(true); }} >
                                <ActivateIcon className="h-5 w-5" /><span className="ml-1">{t('createPermitPage.activateManualPermit')}</span>
                            </button>
                        }
                    </TableItemContextMenu>
                }
            </td>
        </tr>
        { manualActivateModalVisible &&
            <PermitManualActivationModal
                display={manualActivateModalVisible}
                setDisplay={setManualActivateModalVisible}
                permit={permit}
                onSubmit={handleManualActivate}
            />
        }
        { historyModalVisible &&
            <PermitHistoryModal
                display={historyModalVisible}
                setDisplay={setHistoryModalVisible}
                permit={permit}
            />
        }
    </>
    );
}

export default PermitItem;